/**
 * Recommendation types for recommendations queue
 * Maps to Requirement 78: Web UI — Recommendations Queue [MVP]
 */

/**
 * AI tier badge for LLM-generated explanations
 * AC 4: Display LLM-generated explanations with tier badge
 */
export enum AITier {
  T_ML = 'T-ML', // Platform-native lightweight ML
  T1 = 'T1', // Frontier LLM (Claude, GPT-4)
  T2 = 'T2', // Mid-tier LLM
  T3 = 'T3', // Lightweight LLM (offline capable)
}

/**
 * Recommendation category
 * AC 1: Filters for category
 */
// Values must match the real `type` strings the 8 deployed detector
// services (optimization-api/src/services/recommendations/*.service.ts)
// actually emit - this enum previously used plural/placeholder values
// ('idle_resources', 'orphaned_resources') that never matched a single
// real recommendation, so every filter checkbox for those two silently
// matched nothing. Storage/network/database/kubernetes are kept as
// forward-looking categories for detectors that don't exist yet (see
// Docs/ANOMALY-RECOMMENDATIONS-RCA-GAP-CLOSURE-PLAN.md Part 2) - checking
// them today just filters to an empty set, which is honest, not broken.
export enum RecommendationCategory {
  RIGHTSIZING = 'rightsizing',
  IDLE_RESOURCE = 'idle_resource',
  ORPHANED_RESOURCE = 'orphaned_resource',
  OLD_SNAPSHOT = 'old_snapshot',
  ARCHITECTURE_MIGRATION = 'architecture_migration',
  LICENSING_OPTIMIZATION = 'licensing_optimization',
  NONPROD_PREMIUM_WASTE = 'nonprod_premium_waste',
  SCHEDULED_USE = 'scheduled_use',
  COMMITMENT_OPTIMIZATION = 'commitment_optimization',
  STORAGE_OPTIMIZATION = 'storage_optimization',
  NETWORK_OPTIMIZATION = 'network_optimization',
  MODERNIZATION = 'modernization',
  NONPROD_OPTIMIZATION = 'nonprod_optimization',
  AI_OPTIMIZATION = 'ai_optimization',
  PRICING_OPTIMIZATION = 'pricing_optimization',
  DATABASE_OPTIMIZATION = 'database_optimization',
  KUBERNETES_OPTIMIZATION = 'kubernetes_optimization',
  OTHER = 'other',
}

/**
 * Cloud provider
 * AC 1: Filters for cloud
 */
export enum CloudProvider {
  AWS = 'aws',
  AZURE = 'azure',
  GCP = 'gcp',
  OCI = 'oci',
  ON_PREM = 'on_prem',
}

/**
 * Risk tier
 * AC 1: Filters for risk tier
 */
export enum RiskTier {
  LOW = 'low',
  MEDIUM = 'medium',
  HIGH = 'high',
  CRITICAL = 'critical',
}

/**
 * Recommendation lifecycle state (Phase 4.0) - the 12 values
 * optimization-api's recommendation-state.service.ts allows. Labels,
 * colours and allowed moves come from GET /recommendations/states (see
 * components/recommendations/RecommendationLifecycle.tsx); this enum is only
 * the value list for typing.
 */
export enum RecommendationStatus {
  OPEN = 'open',
  IN_REVIEW = 'in_review',
  ACCEPTED = 'accepted',
  TICKETED = 'ticketed',
  FIXING = 'fixing',
  DONE = 'done',
  VERIFYING = 'verifying',
  VERIFIED = 'verified',
  NOT_VERIFIED = 'not_verified',
  REJECTED = 'rejected',
  SNOOZED = 'snoozed',
  RESOLVED = 'resolved',
}

/**
 * Detail level for multi-level views
 * AC 2: Multi-level detail views (L1/L2/L3)
 */
export enum DetailLevel {
  L1 = 'L1', // Executive: savings + confidence + risk
  L2 = 'L2', // Engineer: before/after + charts + contributions
  L3 = 'L3', // Analyst: full calculation + raw signals + evidence chain
}

/**
 * Citation for LLM explanations
 * AC 4: Citation links
 */
export interface Citation {
  id: string;
  source: string;
  url?: string;
  excerpt?: string;
  timestamp?: string;
}

/**
 * LLM-generated explanation
 * AC 4: LLM-generated explanations with tier badge and citation links
 */
export interface LLMExplanation {
  text: string;
  tier: AITier;
  citations: Citation[];
  confidence: number;
  generatedAt: string;
}

/**
 * Resource configuration (before/after)
 * AC 2 L2: before/after
 */
export interface ResourceConfig {
  instanceType?: string;
  vcpu?: number;
  memoryGb?: number;
  storageGb?: number;
  iops?: number;
  networkThroughput?: string;
  [key: string]: string | number | undefined;
}

/**
 * Feature contribution for L2 view
 * AC 2 L2: contributions
 */
export interface FeatureContribution {
  feature: string;
  value: number | string;
  contribution: number; // Percentage contribution to the recommendation
  description: string;
}

/**
 * Raw signal for L3 view
 * AC 2 L3: raw signals
 */
export interface RawSignal {
  signalType: string;
  timestamp: string;
  value: number | string;
  unit?: string;
  source: string;
}

/**
 * Evidence chain for L3 view
 * AC 2 L3: evidence chain
 */
export interface EvidenceChain {
  step: number;
  description: string;
  data: Record<string, unknown>;
  timestamp: string;
}

/**
 * Calculation detail for L3 view
 * AC 2 L3: full calculation
 */
export interface CalculationDetail {
  formula: string;
  variables: Record<string, number>;
  steps: string[];
  result: number;
}

/**
 * Core recommendation data
 * AC 1: Priority (savings × confidence × (1 − risk))
 */
export interface Recommendation {
  id: string;
  category: RecommendationCategory;
  cloudProvider: CloudProvider;
  title: string;
  description: string;
  
  // Priority calculation components (AC 1)
  estimatedMonthlySavings: number;
  confidence: number; // 0-1
  riskScore: number; // 0-1
  priority: number; // savings × confidence × (1 − risk)
  
  riskTier: RiskTier;
  status: RecommendationStatus;
  
  // Target resource
  targetResourceId: string;
  targetResourceName: string;
  targetResourceType: string;
  accountId: string;
  region: string;
  
  // Configuration
  currentConfig: ResourceConfig;
  proposedConfig: ResourceConfig;
  
  // LLM explanation (AC 4)
  explanation?: LLMExplanation;
  
  // Metadata
  createdAt: string;
  updatedAt: string;
  expiresAt?: string;
  createdBy: string;
  
  // L2 details (AC 2)
  utilizationMetrics?: {
    cpuAvg: number;
    cpuP95: number;
    memoryAvg: number;
    memoryP95: number;
    networkAvg?: number;
    iopsAvg?: number;
  };
  featureContributions?: FeatureContribution[];
  
  // L3 details (AC 2)
  rawSignals?: RawSignal[];
  evidenceChain?: EvidenceChain[];
  calculationDetail?: CalculationDetail;
  
  // Action tracking
  actionId?: string;
  ticketId?: string;
  snoozeUntil?: string;
  snoozeReason?: string;
}

/**
 * Recommendation filters
 * AC 1: Filters for category, cloud, risk tier, and status
 */
export interface RecommendationFilters {
  categories?: RecommendationCategory[];
  cloudProviders?: CloudProvider[];
  riskTiers?: RiskTier[];
  statuses?: RecommendationStatus[];
  minSavings?: number;
  maxSavings?: number;
  minConfidence?: number;
  searchQuery?: string;
}

/**
 * Recommendation sort options
 * AC 1: Sorted by priority
 */
export enum RecommendationSortBy {
  PRIORITY = 'priority',
  SAVINGS = 'savings',
  CONFIDENCE = 'confidence',
  RISK = 'risk',
  CREATED_AT = 'createdAt',
}

export enum SortOrder {
  ASC = 'asc',
  DESC = 'desc',
}

/**
 * Recommendation query parameters
 */
export interface RecommendationQueryParams {
  filters?: RecommendationFilters;
  sortBy?: RecommendationSortBy;
  sortOrder?: SortOrder;
  page?: number;
  pageSize?: number;
}

/**
 * Paginated recommendation response
 */
export interface RecommendationListResponse {
  recommendations: Recommendation[];
  total: number;
  page: number;
  pageSize: number;
  totalPages: number;
}

/**
 * Action request for one-click actions
 * AC 3: One-click approve, reject, snooze, and open-ticket actions
 */
export interface RecommendationActionRequest {
  recommendationId: string;
  action: 'approve' | 'reject' | 'snooze' | 'open_ticket';
  reason?: string;
  snoozeDuration?: number; // Days
  ticketingSystem?: 'servicenow' | 'jira' | 'freshservice';
}

/**
 * Action response
 */
export interface RecommendationActionResponse {
  success: boolean;
  message: string;
  actionId?: string;
  ticketId?: string;
  ticketUrl?: string;
}
